import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, AlertCircle, Loader2, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useFaceLandmarker } from '../proctoring/useFaceLandmarker';
import { getYawRatio, getPitchRatio, getGazeRatios } from '../proctoring/proctoringDetector';
import useIsMounted from '../../../../hooks/useIsMounted';
import { toast } from 'sonner';

const ProctoringCalibrationStep = ({ onComplete, onCancel }) => {
  const { faceLandmarker, isLoading: modelsLoading, error: modelError } = useFaceLandmarker();
  
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [calibrationProgress, setCalibrationProgress] = useState(0); // 0 to 100
  const [isCalibrated, setIsCalibrated] = useState(false);
  const [calibratedBaselines, setCalibratedBaselines] = useState(null);
  const [faceCheckError, setFaceCheckError] = useState(null);
  const [stream, setStream] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameIdRef = useRef(null);
  const lastDetectionTimeRef = useRef(0);
  const calibrationFramesRef = useRef([]);
  const isMounted = useIsMounted();

  const startCamera = async () => {
    setCameraError(null);
    setIsCameraActive(false);
    setStream(null);

    try {
      const constraints = {
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (!isMounted.current) {
        mediaStream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = mediaStream;
      setStream(mediaStream);
      setIsCameraActive(true);
    } catch (err) {
      console.error('Error accessing webcam for calibration:', err);
      if (isMounted.current) {
        setCameraError('Could not access webcam. Please verify camera permissions are granted.');
        toast.error('Webcam access denied.');
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setStream(null);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, []);

  const videoCallbackRef = useCallback((node) => {
    if (node && stream) {
      node.srcObject = stream;
      node.play().catch(e => console.error("Error playing video:", e));
    }
    videoRef.current = node;
  }, [stream]);

  // Frame Processing Loop for Calibration
  useEffect(() => {
    if (!faceLandmarker || !isCameraActive || isCalibrated) return;

    const processFrame = () => {
      const video = videoRef.current;
      if (!video) {
        frameIdRef.current = requestAnimationFrame(processFrame);
        return;
      }

      const now = performance.now();
      // Process at ~5 FPS to match active proctoring detector speed
      if (now - lastDetectionTimeRef.current >= 200) {
        lastDetectionTimeRef.current = now;

        if (video.readyState >= 2) {
          const results = faceLandmarker.detectForVideo(video, now);
          handleCalibrationFrame(results);
        }
      }

      frameIdRef.current = requestAnimationFrame(processFrame);
    };

    frameIdRef.current = requestAnimationFrame(processFrame);

    return () => {
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, [faceLandmarker, isCameraActive, isCalibrated]);

  const handleCalibrationFrame = (results) => {
    const faces = results.faceLandmarks || [];
    
    if (faces.length === 0) {
      setFaceCheckError("No face detected. Please face the camera.");
      return;
    }

    if (faces.length > 1) {
      setFaceCheckError("Multiple faces detected. Please make sure you are alone.");
      return;
    }

    setFaceCheckError(null);
    const landmarks = faces[0];
    const list = calibrationFramesRef.current;
    
    const yaw = getYawRatio(landmarks);
    const pitch = getPitchRatio(landmarks);
    const { gazeL, gazeR } = getGazeRatios(landmarks);

    list.push({ yaw, pitch, gazeL, gazeR });
    const progress = Math.min(100, Math.floor((list.length / 15) * 100));
    setCalibrationProgress(progress);

    if (list.length >= 15) {
      const avgYaw = list.reduce((sum, f) => sum + f.yaw, 0) / list.length;
      const avgPitch = list.reduce((sum, f) => sum + f.pitch, 0) / list.length;
      const avgGazeL = list.reduce((sum, f) => sum + f.gazeL, 0) / list.length;
      const avgGazeR = list.reduce((sum, f) => sum + f.gazeR, 0) / list.length;

      const baselines = {
        yaw: avgYaw,
        pitch: avgPitch,
        gazeL: avgGazeL,
        gazeR: avgGazeR
      };

      setIsCalibrated(true);
      setCalibratedBaselines(baselines);
      toast.success("Calibration complete!");
      stopCamera();
    }
  };

  const handleResetCalibration = () => {
    calibrationFramesRef.current = [];
    setCalibrationProgress(0);
    setIsCalibrated(false);
    setCalibratedBaselines(null);
    setFaceCheckError(null);
    startCamera();
  };

  if (modelError || cameraError) {
    return (
      <div className="text-center py-8 text-error space-y-4">
        <AlertCircle className="w-12 h-12 mx-auto text-error animate-bounce" />
        <h3 className="text-lg font-bold">Proctoring Setup Failed</h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          {modelError || cameraError}
        </p>
        <div className="flex justify-center gap-3 pt-2">
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn btn-outline">
              Go Back
            </button>
          )}
          <button type="button" onClick={startCamera} className="btn btn-primary">
            Retry Setup
          </button>
        </div>
      </div>
    );
  }

  if (modelsLoading) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-8 space-y-4">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
        <h3 className="text-lg font-bold">Loading Proctoring AI Models</h3>
        <p className="text-sm text-gray-500 max-w-sm">
          Please wait while the system downloads the visual monitoring assets. This only takes a few seconds.
        </p>
      </div>
    );
  }

  if (isCalibrated && calibratedBaselines) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-8 bg-base-100 rounded-2xl border border-success/20 shadow-xl space-y-6 animate-fade-in my-4">
        <div className="bg-success/10 text-success p-5 rounded-full animate-success-pop shadow-inner">
          <CheckCircle2 className="w-16 h-16" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-base-content">Calibration Complete</h3>
          <p className="text-sm text-gray-500">
            Smart proctoring parameters calibrated successfully!
          </p>
        </div>
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 w-full max-w-sm text-left text-xs font-mono text-gray-600 space-y-1">
          <div>• Center Gaze (Left): {calibratedBaselines.gazeL.toFixed(4)}</div>
          <div>• Center Gaze (Right): {calibratedBaselines.gazeR.toFixed(4)}</div>
          <div>• Yaw Baseline: {calibratedBaselines.yaw.toFixed(4)}</div>
          <div>• Pitch Baseline: {calibratedBaselines.pitch.toFixed(4)}</div>
        </div>
        <div className="flex gap-4 w-full max-w-sm pt-2">
          <button 
            type="button" 
            onClick={handleResetCalibration} 
            className="btn btn-outline flex-1 rounded-xl font-bold text-sm"
          >
            Recalibrate
          </button>
          <button 
            type="button" 
            onClick={() => onComplete(calibratedBaselines)} 
            className="btn btn-primary flex-1 rounded-xl font-bold text-sm gap-2"
          >
            <ShieldCheck className="w-4 h-4" />
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Camera className="w-5 h-5 text-primary" />
          Proctoring Camera Calibration
        </h2>
        <p className="text-sm text-gray-500">
          We need to calibrate your webcam baseline position. Please sit upright, look directly at the screen, and stay still.
        </p>
      </div>

      <div className="relative rounded-2xl overflow-hidden bg-black aspect-video border border-base-300 shadow-xl flex items-center justify-center">
        {!isCameraActive ? (
          <div className="text-white flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm">Accessing webcam...</p>
          </div>
        ) : (
          <>
            <video 
              ref={videoCallbackRef} 
              playsInline 
              muted 
              autoPlay
              className="w-full h-full object-cover scale-x-[-1]"
            />

            {/* Guide Circle Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-56 h-56 md:w-64 md:h-64 rounded-full border-4 border-dashed border-primary shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] flex items-center justify-center relative">
                <div className="absolute inset-[-4px] rounded-full border-2 border-primary animate-ping opacity-25" />
                <span className="absolute bottom-[-45px] text-xs bg-black/85 px-4 py-1.5 rounded-full text-white font-medium shadow-md whitespace-nowrap">
                  Look directly at this circle and stay still
                </span>
              </div>
            </div>

            {/* Error / Warning Alert */}
            {faceCheckError && (
              <div className="absolute top-4 inset-x-4 mx-auto max-w-sm alert alert-error shadow-lg py-2 px-3 text-xs bg-error/90 text-white flex gap-2 rounded-lg justify-center items-center">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{faceCheckError}</span>
              </div>
            )}

            {/* Progress overlay */}
            <div className="absolute bottom-6 left-6 right-6 flex flex-col items-center gap-1 bg-black/60 backdrop-blur-md rounded-xl p-3 border border-white/10 max-w-sm mx-auto">
              <div className="flex justify-between w-full text-[10px] text-white font-semibold">
                <span>Calibrating Head & Eye Position...</span>
                <span>{calibrationProgress}%</span>
              </div>
              <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden">
                <div 
                  className="bg-primary h-full transition-all duration-200" 
                  style={{ width: `${calibrationProgress}%` }}
                />
              </div>
            </div>
          </>
        )}
      </div>
      
      {onCancel && (
        <div className="flex justify-start">
          <button 
            type="button" 
            onClick={onCancel} 
            className="btn btn-outline"
          >
            Cancel Setup
          </button>
        </div>
      )}
    </div>
  );
};

export default ProctoringCalibrationStep;
